import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space, Tag, DatePicker, Modal } from "antd";
import { exportDataAsExcel } from "helpers/helpfulFunctions";
import { toastError, toastSuccess } from "helpers/toasters";
import PrimaryButton from "modules/SubComponents/Buttons/PrimaryButton";
import SecondaryButton from "modules/SubComponents/Buttons/SecondaryButton";
import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import moment from "moment";
import { acceptOffer, getAllOffers } from "Network";
import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

export default function GmAuctionOffers() {
  const [renderMe, setRenderMe] = useState(false);
  const [rowData, setRowData] = useState({});
  const [loading, setLoading] = useState(true);
  const [filteredDataSource, setFilteredDataSource] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedRange, setSelectedRange] = useState(null);
  const [openAcceptModal, setOpenAcceptModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const searchInput = useRef(null);

  const { RangePicker } = DatePicker;

  const handleExportDataToExcel = () => {
    if (isExporting) return;
    setIsExporting(true);

    const baseProps = [
      { title: "Full Name", prop: "fullName" },
      { title: "Mobile", prop: "mobile" },
      { title: "Description", prop: "auctionDescription" },
      { title: "Owner Full Name", prop: "ownerFullName" },
      { title: "Owner Mobile", prop: "ownerMobile" },
      { title: "Type", prop: "type" },
      { title: "Quantity", prop: "quantity" },
      { title: "Offered Price", prop: "offeredPrice" },
      { title: "Date", prop: "creationDate" },
      { title: "Accepted", prop: "isAccepted" },
    ];

    getAllOffers(
      (res) => {
        if (res.success) {
          const formattedData = res.data.map((ele) => ({
            ...ele,
            creationDate: ele.creationDate
              ? moment(ele.creationDate).format("DD-MM-YYYY")
              : "-",
            isAccepted: ele.isAccepted ? "Yes" : "No",
          }));
          exportDataAsExcel(formattedData, {
            baseProps,
            tab: "GM Auction Offers",
          });
        } else {
          toastError(res.message);
        }
        setIsExporting(false);
      },
      (error) => {
        setIsExporting(false);
        toastError(error?.message || "Failed to export data.");
      }
    );
  };

  const handleDateChange = (dates) => {
    setSelectedRange(dates);
    if (dates) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      const dataWithingRange = dataSource.filter((ele) => {
        if (!ele.creationDate) return false;
        const comparisonDate = moment(ele.creationDate).format("YYYY-MM-DD");
        return comparisonDate >= startDate && comparisonDate <= endDate;
      });
      setFilteredDataSource(dataWithingRange);
    } else {
      setFilteredDataSource(dataSource);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleCancel = () => {
    setOpenAcceptModal(false);
    setRowData(null);
    setIsSubmitting(false);
  };

  const handleAcceptOffer = () => {
    const payload = {
      offerId: rowData?.id,
    };

    setIsSubmitting(true);

    acceptOffer(
      payload,
      (success) => {
        toastSuccess(success.message);
        setIsSubmitting(false);
        setRenderMe((pre) => !pre);
        handleCancel();
      },
      (error) => {
        setIsSubmitting(false);
        toastError(error.message || "An error occurred!");
      }
    );
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  const getColumnDateProps = (dataIndex) => ({
    filterDropdown: () => (
      <div
        style={{
          padding: 8,
        }}
        className="range-picker-holder"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={selectedRange}
          allowClear
          popupClassName="all-orders-date-picker"
          inputReadOnly={false}
          onChange={handleDateChange}
          onKeyDown={(e) => e.preventDefault()}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <FilterOutlined style={selectedRange ? { color: "blue" } : {}} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  const columns = [
    {
      title: "FULL NAME",
      dataIndex: "fullName",
      key: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      title: "MOBILE",
      dataIndex: "mobile",
      key: "mobile",
      ...getColumnSearchProps("mobile"),
    },
    {
      title: "DESCRIPTION",
      dataIndex: "auctionDescription",
      key: "auctionDescription",
      ...getColumnSearchProps("auctionDescription"),
    },
    {
      title: "OWNER FULL NAME",
      dataIndex: "ownerFullName",
      key: "ownerFullName",
      ...getColumnSearchProps("ownerFullName"),
    },
    {
      title: "OWNER MOBILE",
      dataIndex: "ownerMobile",
      key: "ownerMobile",
      ...getColumnSearchProps("ownerMobile"),
      render: (mobile) => (mobile ? mobile : "-"),
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      ...getColumnSearchProps("type"),
    },
    {
      title: "QUANTITY",
      dataIndex: "quantity",
      key: "quantity",
      ...getColumnSearchProps("quantity"),
    },
    {
      title: "OFFERED PRICE",
      dataIndex: "offeredPrice",
      key: "offeredPrice",
      render: (value) => `${value} EGP`,
      ...getColumnSearchProps("offeredPrice"),
    },
    {
      title: "DATE",
      dataIndex: "creationDate",
      key: "creationDate",
      ...getColumnDateProps("creationDate"),
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "ACCEPTED",
      dataIndex: "isAccepted",
      key: "isAccepted",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      key: "id",
      title: "ACTIONS",
      render: (_, row) => {
        return (
          <PrimaryButton
            buttonName={"Accept"}
            disableMe={row?.acceptDisabled}
            onClick={() => {
              setRowData(row);
              setOpenAcceptModal(true);
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    getAllOffers(
      (res) => {
        setLoading(false);
        const updatedData = res.data.map((ele) => {
          // Format creation date for display
          if (ele.creationDate) {
            ele.creationDate = new Date(ele.creationDate).toISOString();
          }
          return ele;
        });
        setDataSource(updatedData);
        setFilteredDataSource(updatedData);
      },
      () => {}
    );
  }, [renderMe]);

  return (
    <section className="users auctions">
      <div className="users__all-contents">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="GM Auction Offers" />
            <SubHeader bgColor="#356ab3" title="All Offers" />
            <div className="users__table grid-table mrg-ye-50">
              <GridTable
                allowExport
                apiExport
                isExporting={isExporting}
                onExport={handleExportDataToExcel}
                title="GM Auction Offers"
                loading={loading}
                columns={columns}
                // dataSource={dataSource}
                dataSource={filteredDataSource}
                rowId={"id"}
                rowClassName={(record) =>
                  record.showRedFlag
                    ? "auctions__table-row auctions__table-row--red-flag"
                    : "auctions__table-row"
                }
              />
            </div>
          </div>
        </div>
      </div>

      {openAcceptModal && (
        <Modal open={openAcceptModal} footer={null} onCancel={handleCancel}>
          <div className="mt-4 mb-4 text-center">
            <h5>
              You are about to accept an offer from {rowData?.fullName} for{" "}
              {rowData?.offeredPrice} EGP
            </h5>
          </div>
          <div className="d-flex justify-content-center">
            <Space>
              <SecondaryButton
                onClick={handleCancel}
                text={"Cancel"}
                buttonName={"Cancel"}
              />
              <PrimaryButton
                buttonName={"Accept"}
                type="submit"
                loading={isSubmitting}
                onClick={handleAcceptOffer}
                // disableMe={mode === "edit" && !hasChanges}
                // text={"Submit"}
              />
            </Space>
          </div>
        </Modal>
      )}
    </section>
  );
}
